import React, { useContext, useEffect, useState, useRef } from "react";
import ProfilePicture from "../../../resources/icons/profile-circle-disabled.svg";
import AuthContext from "../../../store/auth-context";
import { POST, GET } from "../../../service/CRUDService";
import EntityConstants from "../../../configs/EntityConstants";
import Toast from "../../../common/Toast/Toast";
import EmailIcon from "../../../resources/icons/icons8-email-48_purple.svg";
import { FileText } from "lucide-react";
import CfLoader from "../../../common/CfLoader/CfLoader";

const Profile = () => {
  const [isFieldChanged, setIsFieldChanged] = useState(false);
  const [userType, setUserType] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [initialData, setInitialData] = useState({});
  const [phoneError, setPhoneError] = useState("");

  const authContext = useContext(AuthContext);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    gender: "N/A",
    email: "",
    date_of_birth: "",
    phone: "",
    profile_photo: null,
  });

  const [previewSrc, setPreviewSrc] = useState(ProfilePicture);

  const validatePhoneNumber = (phone) => {
    if (!phone) {
      return { isValid: false, error: "Phone number is required" };
    }

    const phoneRegex = /^(\+91[\s-]?|91[\s-]?|0)?[6-9]\d{9}$/;
    const isValid = phoneRegex.test(phone);

    return {
      isValid,
      error: isValid
        ? ""
        : "Please enter a valid phone number. Example: +91 9999999999",
    };
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        setIsLoading(true);
        const response = await GET(EntityConstants.CURRENT_USER);
        if (response.ok) {
          const userData = await response.json();
          setUserType(userData.type);

          const userGender =
            userData.gender === null || userData.gender === undefined
              ? "N/A"
              : userData.gender;

          const userFormData = {
            first_name: userData.first_name || "",
            last_name: userData.last_name || "",
            gender: userGender,
            email: userData.email || "",
            date_of_birth: userData.date_of_birth || "",
            phone: userData.phone || "",
            profile_photo: userData.profile_photo || null,
          };

          setInitialData(userFormData);
          setFormData(userFormData);

          // Validate initial phone data
          if (!userFormData.phone) {
            setPhoneError("Phone number is required");
          }

          if (userData.profile_photo) {
            setPreviewSrc(userData.profile_photo);
          }

          if (
            authContext.userDetails &&
            Object.keys(authContext.userDetails).length === 0
          ) {
            authContext.createUserProfileDetails(userData);
          }
        }
      } catch (error) {
        console.error("Error fetching user data:", error);
        Toast({
          type: "error",
          title: "Error",
          message: "Failed to load profile data",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [authContext]);

  useEffect(() => {
    const hasChanges = Object.keys(formData).some((key) => {
      if (key === "profile_photo") {
        return (
          formData[key] !== initialData[key] &&
          formData[key] !== null &&
          formData[key] !== initialData.profile_photo
        );
      }
      return formData[key] !== initialData[key];
    });

    setIsFieldChanged(hasChanges);
  }, [formData, initialData]);

  const handleChange = (event) => {
    const { name, value, type, files } = event.target;

    if (type === "file") {
      const file = files[0];
      if (file) {
        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = () => {
          const base64String = reader.result;
          setFormData((prevData) => ({
            ...prevData,
            [name]: base64String,
          }));
          setPreviewSrc(base64String);
        };
      }
    } else {
      setFormData((prevData) => ({
        ...prevData,
        [name]: value,
      }));

      if (name === "phone") {
        const validation = validatePhoneNumber(value);
        setPhoneError(validation.error);
      }
    }
  };

  const handlePhoneInput = (event) => {
    let value = event.target.value.replace(/[^\d+]/g, "");

    if (
      value.startsWith("+") &&
      value.length > 3 &&
      value.indexOf(" ") === -1
    ) {
      value = value.substring(0, 3) + " " + value.substring(3);
    }

    setFormData((prevData) => ({
      ...prevData,
      phone: value,
    }));

    const validation = validatePhoneNumber(value);
    setPhoneError(validation.error);
  };

  const isFormValid = () => {
    const phoneValidation = validatePhoneNumber(formData.phone);
    return isFieldChanged && phoneValidation.isValid;
  };

  const handleSave = async () => {
    const phoneValidation = validatePhoneNumber(formData.phone);
    if (!phoneValidation.isValid) {
      Toast({
        type: "error",
        title: "Validation Error",
        message: phoneValidation.error,
      });
      return;
    }

    const payload = {};

    Object.keys(formData).forEach((key) => {
      if (key === "profile_photo") {
        if (
          formData[key] !== initialData[key] &&
          formData[key] !== null &&
          formData[key] !== initialData.profile_photo
        ) {
          payload[key] = formData[key];
        }
      } else if (formData[key] !== initialData[key]) {
        payload[key] = formData[key];
      }
    });

    if (Object.keys(payload).length === 0) {
      Toast({
        type: "warning",
        title: "Info",
        message: "No changes to save",
      });
      return;
    }

    try {
      const response = await POST(
        `${EntityConstants.CURRENT_USER}update`,
        payload
      );
      const result = await response.json();

      if (response.ok) {
        Toast({
          type: "success",
          title: "Success",
          message: "Profile updated successfully",
        });
        setInitialData(formData);
        setIsFieldChanged(false);
        setPhoneError("");
      } else {
        if (result.error) {
          let errorMessage = "Failed to update profile";

          if (result.error.phone) {
            errorMessage = `Phone: ${result.error.phone.join(", ")}`;
            setPhoneError(result.error.phone.join(", "));
          } else if (result.message) {
            errorMessage = result.message;
          }

          Toast({
            type: "error",
            title: "Validation Error",
            message: errorMessage,
          });
        } else {
          Toast({
            type: "error",
            title: "Error",
            message: "Failed to update profile",
          });
        }
      }
    } catch (error) {
      console.log("Error:", error);
      Toast({
        type: "error",
        title: "Error",
        message: "Failed to update profile",
      });
    }
  };

  function ProfilePicUpload({
    id,
    altText,
    onFileUpload,
    logoHeading,
    logoSubHeading,
  }) {
    const [dragActive, setDragActive] = useState(false);
    const fileInputRef = useRef(null);

    const handleFileChange = (event) => {
      const file = event.target.files[0];
      if (!file) {
        return;
      }
      processFile(file, event.target);
    };

    const handleDragOver = (event) => {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(true);
    };

    const handleDragLeave = (event) => {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
    };

    const handleDrop = (event) => {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      const file = event.dataTransfer.files[0];
      if (!file) {
        return;
      }
      processFile(file, fileInputRef.current);
    };

    const handleKeyDown = (event) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        document.getElementById(id).click();
      }
    };

    const processFile = (file, inputElement) => {
      const allowedFileTypes = ["png", "jpeg", "jpg"];
      const allowedMimeTypes = ["image/png", "image/jpeg", "image/jpg"];

      if (file) {
        const extension = file.name.split(".").pop().toLowerCase();
        const isValidType = allowedFileTypes.includes(extension);
        const isValidMime = allowedMimeTypes.includes(file.type);

        if (isValidType && isValidMime) {
          const maxSize = 5 * 1024 * 1024;
          if (file.size > maxSize) {
            Toast({
              type: "error",
              title: "Error",
              message: "File size should be less than 5MB",
            });
            if (inputElement) {
              inputElement.value = "";
            }
            return;
          }
          onFileUpload(file);
        } else {
          Toast({
            type: "error",
            title: "Error",
            message: "Please select a valid image format (PNG, JPEG, JPG).",
          });
          if (inputElement) {
            inputElement.value = "";
          }
        }
      }
    };

    return (
      <div
        className={`flex gap-4 py-6 px-4 mt-2 cursor-pointer text-gray-500 bg-white rounded-lg outline-dotted outline-gray-300 outline-offset-2 shadow-sm ${
          dragActive ? "bg-gray-100" : ""
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
      >
        <input
          type="file"
          accept=".png,.jpeg,.jpg,image/png,image/jpeg,image/jpg,image"
          onChange={handleFileChange}
          className="hidden"
          id={id}
          ref={fileInputRef}
          loading="lazy"
        />
        <label htmlFor={id} className="cursor-pointer flex flex-row w-full">
          <img
            loading="lazy"
            src="https://cdn.builder.io/api/v1/image/assets/TEMP/e6c376ce7ba432a6c2a0781570893c28440a280d4825eeedee07208e78e63d79?apiKey=1ca7de1f58f745cd95efc0640d2ef737&"
            alt={altText}
            className="shrink-0 my-auto w-9 mr-4 aspect-square"
          />
          <div className="flex flex-col">
            <div className="text-sm">
              <span className="font-semibold text-indigo-700">
                {logoHeading} {logoSubHeading}
              </span>
            </div>
            <div className="mt-2 text-xs">PNG, JPEG, JPG (max 5MB)</div>
          </div>
        </label>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="w-full flex justify-center items-center">
        <CfLoader />
      </div>
    );
  }

  const getSaveButtonTitle = () => {
    if (!isFieldChanged) return "Make changes to Save";
    if (phoneError) return "Fix phone number to save";
    if (!formData.phone) return "Phone number is required";
    return "Save";
  };

  return (
    <div className="flex flex-col items-center w-full mt-12 md:mt-20 py-12 px-5 bg-cfSecondary min-h-screen">
      <div className="text-xl font-semibold text-gray-900 w-full mb-4 flex justify-between items-center">
        <h1>Profile</h1>
        <button
          disabled={!isFormValid()}
          onClick={handleSave}
          title={getSaveButtonTitle()}
          className="px-4 py-2 rounded-lg bg-indigo-700 text-base text-white disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          Save
        </button>
      </div>
      <div className="border rounded-xl w-full relative">
        <div className="px-4 pb-4">
          <div className="flex flex-col md:flex-row gap-2 md:gap-0 items-center mb-2 md:mb-4 justify-between py-4">
            <div className="flex items-center gap-8 justify-center">
              <img
                src={previewSrc}
                className="w-24 h-24 rounded-full"
                alt="Profile"
              />
            </div>
            <ProfilePicUpload
              id="fileInput"
              altText="Profile Upload"
              logoHeading={
                formData.profile_photo
                  ? "Change Uploaded Profile Photo"
                  : "Upload Profile Photo"
              }
              logoSubHeading=""
              onFileUpload={(file) => {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onloadend = () => {
                  const base64String = reader.result;
                  setFormData((prevData) => ({
                    ...prevData,
                    profile_photo: base64String,
                  }));
                  setPreviewSrc(base64String);
                };
              }}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <span className="max-w-lg">
              <label
                htmlFor="first_name"
                className="text-sm font-semibold text-black"
              >
                First Name
              </label>
              <input
                type="text"
                placeholder="First Name"
                className="border rounded-lg p-2 w-full text-sm placeholder:text-sm"
                name="first_name"
                id="first_name"
                value={formData.first_name}
                onChange={handleChange}
              />
            </span>

            <span className="max-w-lg">
              <label
                htmlFor="last_name"
                className="text-sm font-semibold text-black"
              >
                Last Name
              </label>
              <input
                type="text"
                placeholder="Last Name"
                className="border rounded-lg p-2 w-full text-sm placeholder:text-sm"
                name="last_name"
                id="last_name"
                value={formData.last_name}
                onChange={handleChange}
              />
            </span>
            <span className="max-w-lg">
              <label
                htmlFor="gender"
                className="text-sm font-semibold text-black"
              >
                Gender
              </label>
              <select
                name="gender"
                id="gender"
                value={formData.gender === "N/A" ? "" : formData.gender}
                onChange={handleChange}
                className="border rounded-lg p-2 w-full text-sm placeholder:text-sm"
              >
                <option value="" disabled>
                  Select Gender
                </option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Others">Others</option>
              </select>
              {formData.gender === "N/A" && (
                <div className="text-gray-500 text-sm mt-1">N/A</div>
              )}
            </span>
            <span className="max-w-lg">
              <label
                htmlFor="phone"
                className="text-sm font-semibold text-black"
              >
                Mobile <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                placeholder="+91 9999999999"
                className={`border rounded-lg p-2 w-full text-sm placeholder:text-sm ${
                  phoneError ? "border-red-500" : ""
                }`}
                name="phone"
                id="phone"
                value={formData.phone}
                onChange={handleChange}
                onInput={handlePhoneInput}
                required
              />
              {phoneError && (
                <p className="text-red-500 text-xs mt-1">{phoneError}</p>
              )}
            </span>
          </div>
        </div>
      </div>
      <div className="self-start mt-4">
        <span className="flex items-center gap-2 border rounded-2xl text-sm p-3 ">
          <span className="w-[2rem] sm:w-[5.5rem] h-[2rem] sm:h-[5.5rem] rounded-full bg-[#4338CA] flex items-center justify-center">
            <img
              src={EmailIcon}
              alt=""
              className="w-[2rem] sm:w-[5.5rem] h-[2rem] sm:h-[5.5rem]"
            />
          </span>
          <span className="text-black text-sm">
            <p className="flex items-center gap-2">
              <span className="font-bold">
                {formData.first_name + " " + formData.last_name}
              </span>
              {userType && (
                <span className="flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium text-white bg-cfPrimaryButton">
                  <FileText className="w-3 h-3 text-white" />
                  {userType}
                </span>
              )}
            </p>
            <p className="mt-2">{formData.email}</p>
          </span>
        </span>
      </div>
    </div>
  );
};

export default Profile;
